import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TeacherPresence } from "@/components/teacher/TeacherPresence";
import { Whiteboard } from "@/components/whiteboard/Whiteboard";
import { useUiStore } from "@/stores/uiStore";
import { useLessonRunner } from "./useLessonRunner";
import { Play, Send } from "lucide-react";

export function ClassroomPage() {
  const presence = useUiStore((s) => s.teacherPresence);
  const [searchParams] = useSearchParams();
  const [topic, setTopic] = useState(searchParams.get("topic") ?? "Newton's First Law");
  const [answer, setAnswer] = useState("");
  const { start, submitAnswer, status, pendingQuestion, summary, error } = useLessonRunner();

  const handleSubmitAnswer = () => {
    if (!answer.trim()) return;
    submitAnswer(answer.trim());
    setAnswer("");
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper pb-28 lg:pb-0">
      <div className="mx-auto grid w-full max-w-6xl flex-1 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_280px] lg:items-start">
        <div className="space-y-4">
          <div className="lg:hidden">
            <TeacherPresence presence={presence} />
          </div>

          <Whiteboard />

          {error && (
            <div className="rounded-2xl border border-incorrect/40 bg-incorrect/10 p-4 font-body text-sm text-incorrect">
              Lesson couldn't start: {error}. If this is a fresh deploy, confirm MISTRAL_API_KEY
              is set (wrangler pages secret put MISTRAL_API_KEY) and the R2 binding exists.
            </div>
          )}

          {status === "idle" && (
            <div className="glass flex flex-col gap-3 rounded-2xl p-4 sm:flex-row sm:items-center">
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="What should the teacher cover?"
                className="flex-1 rounded-lg border border-border bg-transparent px-3 py-2 font-body text-sm outline-none focus:border-marker"
              />
              <button
                onClick={() => start(topic)}
                className="flex items-center justify-center gap-2 rounded-lg bg-marker px-4 py-2 font-body text-sm font-medium text-paper"
              >
                <Play size={16} /> Start lesson
              </button>
            </div>
          )}

          {status === "loading" && (
            <p className="font-body text-sm text-ink/60">Preparing the lesson…</p>
          )}

          {pendingQuestion && (
            <div className="glass space-y-3 rounded-2xl p-4">
              <p className="font-body text-sm font-medium">{pendingQuestion}</p>
              <div className="flex gap-2">
                <input
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmitAnswer()}
                  placeholder="Your answer"
                  className="flex-1 rounded-lg border border-border bg-transparent px-3 py-2 font-body text-sm outline-none focus:border-marker"
                  autoFocus
                />
                <button
                  onClick={handleSubmitAnswer}
                  aria-label="Submit answer"
                  className="rounded-lg bg-marker px-3 py-2 text-paper"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}

          {summary && (
            <div className="glass rounded-2xl p-4 font-body text-sm">
              <p className="font-medium text-correct">Lesson complete</p>
              <p className="mt-1 text-ink/70">{summary}</p>
            </div>
          )}
        </div>

        <aside className="hidden lg:block lg:sticky lg:top-24">
          <TeacherPresence presence={presence} />
        </aside>
      </div>
    </div>
  );
}
